// ⚠️  FILE GENERATO AUTOMATICAMENTE — NON MODIFICARE A MANO.
// Fonte unica: shared/bookingRules.js (vedi lì per spiegazione e commenti).
// Rigenerato da scripts/sync-shared-rules.js ad ogni build/deploy.
// Se hai bisogno di cambiare questa logica, modifica SOLO shared/bookingRules.js.

/**
 * shared/bookingRules.js — FONTE UNICA della regola più critica del prodotto
 * (quando un'auto è occupata), estratta durante l'audit del 2026-09-20 (punto
 * #6). PRIMA di questo file la stessa regola viveva in tre punti scritti a
 * mano — src/utils/bookingUtils.js (mai realmente chiamata), BookingModal.js
 * (inline), functions/index.js:confirmOnlineBooking (inline) — coincidenti
 * per puro caso, senza garanzia che lo restassero al prossimo cambio.
 *
 * QUESTO FILE NON VIENE IMPORTATO DIRETTAMENTE:
 *   - React (Create React App) non può importare file fuori da src/
 *     (ModuleScopePlugin lo blocca a build-time).
 *   - Firebase Functions include in deploy SOLO la cartella functions/, non
 *     la root del repo.
 * Per questo esiste `npm run sync-shared` (script/sync-shared-rules.js), che
 * copia QUESTO file byte-per-byte in:
 *   - src/utils/bookingRules.generated.js   (per il frontend)
 *   - functions/bookingRules.generated.js   (per le Cloud Function)
 * Lo script gira automaticamente come "pre" step di `npm start`, `npm run
 * build` (root) e `firebase deploy --only functions` (predeploy in
 * firebase.json). Non modificare MAI i due file *.generated.js a mano: le
 * modifiche vanno fatte SOLO qui, poi propagate rilanciando lo script (o
 * semplicemente eseguendo build/deploy, che lo fa da soli).
 *
 * Sintassi: CommonJS puro (module.exports), compatibile sia con Node
 * (Cloud Functions) sia con il bundler di Create React App (interop CJS).
 */

// Stati che bloccano le date nel calendario (impediscono nuove prenotazioni
// sovrapposte sulla stessa auto). Include 'auto_in_riconsegna': l'auto non è
// ancora fisicamente rientrata finché l'admin non conferma il rientro.
const STATI_BLOCCANTI = [
  'prenotazione_confermata',
  'in_attesa_ritiro',
  'auto_ritirata',
  'auto_in_riconsegna',
];

/**
 * Data di fine "effettiva" ai fini del controllo di disponibilità.
 * Se lo stato è 'auto_in_riconsegna', l'auto non è ancora stata confermata
 * come riconsegnata dall'admin → blocca tutte le date future fino a conferma
 * (restituendo una data lontana, 2099-12-31, invece della dataFine reale).
 */
function getEffectiveEndDate(booking) {
  if (booking && booking.stato === 'auto_in_riconsegna') return '2099-12-31';
  return booking ? booking.dataFine : undefined;
}

/**
 * Verifica se due intervalli [start1, end1) e [start2, end2) si sovrappongono.
 *
 * REGOLA (fine INCLUSIVA per l'intervallo ESISTENTE): il giorno di riconsegna
 * di una prenotazione esistente resta bloccante finché l'admin non conferma
 * "Riconsegna avvenuta" — un nuovo noleggio non può iniziare quel giorno
 * stesso. Non è invece bloccante nella direzione opposta: una nuova
 * prenotazione può concludersi lo stesso giorno in cui un'altra, esistente,
 * inizia (data di ritiro esclusiva sul lato "nuovo"). Verificata come
 * intenzionale nell'audit 2026-09-20, punto #6 (bonus asimmetria).
 *
 * Le date vanno passate come stringhe 'YYYY-MM-DD' (o Date/timestamp
 * comparabili con <= e >): il confronto è puramente ordinale, non fa parsing
 * di fuso orario — quello è responsabilità del chiamante (vedi
 * utils/helpers.js:parseLocalDate lato frontend).
 */
function datesOverlap(start1, end1, start2, end2) {
  return start1 <= end2 && end1 > start2;
}

/**
 * Verifica se un intervallo [startDate, endDate) genera un conflitto con una
 * lista di prenotazioni esistenti sulla stessa auto. Wrapper di comodo sopra
 * datesOverlap + getEffectiveEndDate + STATI_BLOCCANTI, per chi vuole passare
 * direttamente l'array di prenotazioni invece di iterare a mano (oggi usato
 * solo lato frontend, ma disponibile identico anche lato server).
 */
function checkDateConflict(startDate, endDate, bookings, carId) {
  if (!bookings || bookings.length === 0) return false;
  return bookings.some((booking) => {
    if (booking.carId !== carId) return false;
    if (!STATI_BLOCCANTI.includes(booking.stato)) return false;
    return datesOverlap(startDate, endDate, booking.dataInizio, getEffectiveEndDate(booking));
  });
}

module.exports = { STATI_BLOCCANTI, getEffectiveEndDate, datesOverlap, checkDateConflict };
